/** 面板身份与读取状态。身份包含城市、对象和分页，不让迟到结果恢复旧面板。 */
export interface PanelReadContext { key: string; label: string; close: 'dashboard' | 'entity' }
export interface PanelReadView { context: PanelReadContext | null; phase: 'hidden' | 'loading' | 'updating' | 'ready'; replace: boolean }

export function createPanelReadState(render: (view: PanelReadView) => void) {
  let selected: string | null = null;
  let visible: string | null = null;
  let completed: string | null = null;
  return {
    select(context: PanelReadContext | null) {
      selected = context?.key ?? null;
      if (!context) { visible = completed = null; render({ context: null, phase: 'hidden', replace: false }); }
    },
    pending(context: PanelReadContext) {
      if (context.key !== selected) return;
      const replace = visible !== context.key;
      if (replace) { visible = context.key; completed = null; }
      render({ context, phase: completed === context.key ? 'updating' : 'loading', replace });
    },
    ready(context: PanelReadContext, updating = false) {
      if (context.key !== selected) return;
      visible = completed = context.key;
      render({ context, phase: updating ? 'updating' : 'ready', replace: false });
    },
  };
}

const escape = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
export function panelPendingHTML(context: PanelReadContext): string {
  const close = context.close === 'dashboard' ? 'data-closetab' : 'data-close';
  return `<div class="sb-ph"><button class="sb-x" ${close} title="关闭" aria-label="关闭面板">×</button><h3>${escape(context.label)}</h3></div>` +
    '<div class="sb-body"><div class="sb-empty">正在读取所选资料…</div></div>';
}

/** 同一对象刷新只改独立状态条，不替换正文、滚动位置或已展开明细。 */
export function applyPanelReadView(host: HTMLElement, view: PanelReadView): void {
  if (!view.context) {
    host.removeAttribute('aria-busy');
    host.querySelector('[data-panel-read-status]')?.remove();
    return;
  }
  if (view.replace) host.innerHTML = panelPendingHTML(view.context);
  host.setAttribute('aria-busy', String(view.phase !== 'ready'));
  let status = host.querySelector<HTMLElement>('[data-panel-read-status]');
  if (!status) {
    status = host.ownerDocument.createElement('div');
    status.dataset.panelReadStatus = '';
    status.className = 'sb-query-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    const head = host.querySelector('.sb-ph');
    // 放在头部内部,不在分页正文前插入额外兄弟节点;原DOM增量更新依赖兄弟顺序保留滚动。
    if (head) head.append(status); else host.prepend(status);
  }
  // 只在读取中、更新中写出来;读完了不常驻「已读取」(标题栏里一直挂着一行状态,看着像没读完)。
  // 画出来是标题左边角上的一个小点(sandbox.css),不占地方:原来是标题栏里的一行字,一刷新标题栏就高一行、矮一行,
  // 下面的正文跟着上下跳。不用 hidden 切,点的出现交给 CSS 延迟,刷新快的那几下不闪。
  const phase = { loading: '读取中', updating: '更新中', ready: '', hidden: '' }[view.phase];
  const text = phase ? `${view.context.label} · ${phase}` : '';
  if (status.textContent !== text) status.textContent = text;
  status.dataset.state = view.phase;
}
